import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { 
  FileText, 
  CheckCircle, 
  XCircle, 
  Clock, 
  User, 
  Trash2,
  Edit,
  RefreshCw,
  Shield,
  AlertTriangle
} from 'lucide-react';

const DocumentActionRequestsPanel = () => {
  const { api, user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');

  // Check if user can manage these requests
  const canManage = ['director', 'managing_director', 'admin', 'hr'].includes(user?.role) || 
                    user?.email?.toLowerCase() === 'admin@icrcl.co.uk';

  const fetchRequests = useCallback(async () => {
    if (!canManage) {
      setLoading(false);
      return;
    }

    try {
      const response = await api.get('/uploads/pending-requests');
      setRequests(response.data.requests || []);
    } catch (error) {
      console.error('Failed to fetch requests:', error);
    } finally {
      setLoading(false);
    }
  }, [api, canManage]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const handleApprove = async (requestId) => {
    setProcessing(requestId);
    try {
      await api.post(`/uploads/approve-action/${requestId}`);
      toast.success('Document action approved and executed');
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve request');
    } finally {
      setProcessing(null);
    }
  };

  const handleReject = async () => {
    if (!selectedRequest) return;

    setProcessing(selectedRequest.id);
    try {
      await api.post(`/uploads/reject-action/${selectedRequest.id}?rejection_reason=${encodeURIComponent(rejectionReason)}`);
      toast.success('Document action request rejected');
      setShowRejectDialog(false);
      setSelectedRequest(null);
      setRejectionReason('');
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject request');
    } finally {
      setProcessing(null);
    }
  };

  if (!canManage) return null;
  if (loading) return null;
  if (requests.length === 0) return null;

  return (
    <>
      <Card className="card-icrcl border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50 mb-6">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
              <FileText className="w-5 h-5 text-amber-600" />
            </div>
            <div className="flex-1">
              <CardTitle className="text-lg text-amber-800">
                Document Action Requests ({requests.length})
              </CardTitle>
              <CardDescription className="text-amber-600">
                Staff requesting to delete or amend their documents
              </CardDescription>
            </div>
            <Button variant="ghost" size="sm" onClick={fetchRequests}>
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {requests.map((request) => (
              <div
                key={request.id}
                className="p-4 bg-white rounded-lg border border-amber-100"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <User className="w-4 h-4 text-slate-500" />
                      <span className="font-medium text-slate-900">
                        {request.requested_by_name}
                      </span>
                      <Badge variant="outline" className="text-xs">
                        {request.requested_by_role?.replace(/_/g, ' ')}
                      </Badge>
                      <Badge 
                        variant="outline" 
                        className={request.action === 'delete' ? 'text-red-600 border-red-200' : 'text-blue-600 border-blue-200'}
                      >
                        {request.action === 'delete' ? (
                          <><Trash2 className="w-3 h-3 mr-1" /> Delete</>
                        ) : (
                          <><Edit className="w-3 h-3 mr-1" /> Amend</>
                        )}
                      </Badge>
                    </div>
                    <p className="text-sm text-slate-600 mb-1">
                      Document: <strong>{request.attachment_filename}</strong>
                    </p>
                    <p className="text-xs text-slate-500 mb-2">
                      Category: {request.attachment_category}
                    </p>
                    <div className="bg-slate-50 p-2 rounded text-sm text-slate-600">
                      <strong>Reason:</strong> {request.reason}
                    </div>
                    <p className="text-xs text-slate-400 mt-2">
                      Requested: {new Date(request.requested_at).toLocaleString('en-GB')}
                    </p>
                  </div>
                  <div className="flex flex-col gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-red-200 text-red-600 hover:bg-red-50"
                      onClick={() => {
                        setSelectedRequest(request);
                        setShowRejectDialog(true);
                      }}
                      disabled={processing === request.id}
                    >
                      <XCircle className="w-4 h-4 mr-1" />
                      Reject
                    </Button>
                    <Button
                      size="sm"
                      className="bg-green-600 hover:bg-green-700"
                      onClick={() => handleApprove(request.id)}
                      disabled={processing === request.id}
                    >
                      {processing === request.id ? (
                        <Clock className="w-4 h-4 mr-1 animate-spin" />
                      ) : (
                        <CheckCircle className="w-4 h-4 mr-1" />
                      )}
                      Approve
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Reject Dialog */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="w-5 h-5" />
              Reject Document Request
            </DialogTitle>
            <DialogDescription>
              Please provide a reason for rejecting this request
            </DialogDescription>
          </DialogHeader>

          {selectedRequest && (
            <div className="py-4 space-y-4">
              <div className="bg-slate-50 p-3 rounded-lg">
                <p className="text-sm">
                  <strong>Staff:</strong> {selectedRequest.requested_by_name}
                </p>
                <p className="text-sm">
                  <strong>Document:</strong> {selectedRequest.attachment_filename}
                </p>
                <p className="text-sm">
                  <strong>Action:</strong> {selectedRequest.action}
                </p>
              </div>

              <div className="space-y-2">
                <Label>Rejection Reason (Optional)</Label>
                <Textarea
                  placeholder="Explain why this request is being rejected..."
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  rows={3}
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRejectDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleReject}
              className="bg-red-600 hover:bg-red-700"
              disabled={processing}
            >
              <XCircle className="w-4 h-4 mr-2" />
              Reject Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default DocumentActionRequestsPanel;
